import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Code, Menu, X, MapPin, Mail, Phone, Twitter, Linkedin, Instagram, Github, Moon, Sun, ArrowRight, Globe } from 'lucide-react';
import { useSiteContent, Language } from '../context/SiteContext';
import { ScrollProgress } from './ScrollProgress';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { generalSettings, siteContent, theme, toggleTheme, language, setLanguage, t } = useSiteContent();
  console.log('Layout Rendering. SiteContent:', { hasContent: !!siteContent, lang: language });

  const navLinks = [
    { label: t.nav.services, path: '/services' },
    { label: t.nav.projects, path: '/projects' },
    { label: t.nav.about, path: '/about' },
    { label: t.nav.contact, path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const LanguageSelector = () => (
    <div className="flex items-center gap-1 bg-gray-100 dark:bg-white/5 rounded-lg p-1">
      {(['tr', 'en', 'it'] as Language[]).map((lang) => (
        <button
          key={lang}
          onClick={() => setLanguage(lang)}
          className={`px-2 py-1 text-xs font-bold rounded transition-all ${language === lang
            ? 'bg-white dark:bg-primary text-slate-900 dark:text-white shadow-sm'
            : 'text-gray-500 hover:text-slate-900 dark:hover:text-white'
            }`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background-light dark:bg-background-dark transition-colors duration-300">
      <ScrollProgress />

      {/* Skip to Content */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-[60] px-4 py-2 bg-primary text-white font-bold rounded-lg shadow-lg">
        {t.common?.skipToContent || 'Skip to content'}
      </a>

      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${scrolled
          ? 'h-16 bg-white/80 dark:bg-background-dark/80 backdrop-blur-md border-b border-gray-200 dark:border-border-dark shadow-sm'
          : 'h-20 bg-transparent border-b border-transparent'
          }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex h-full items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group relative z-50" aria-label="GreySoftware Home">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white shadow-lg shadow-primary/30 group-hover:scale-105 transition-transform">
                <Code size={20} aria-hidden="true" />
              </div>
              <span className={`text-xl font-bold tracking-tight transition-colors ${scrolled ? 'text-slate-900 dark:text-white' : 'text-slate-900 dark:text-white'}`}>
                GreySoftware
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1 bg-white/50 dark:bg-white/5 backdrop-blur-sm px-2 py-1.5 rounded-full border border-gray-200 dark:border-white/10" aria-label="Main Navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${isActive(link.path)
                    ? 'bg-white dark:bg-surface-dark text-primary shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                    }`}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3 relative z-50">
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>

              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-full text-gray-500 hover:text-primary hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10 transition-all"
                aria-label={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {theme === 'dark' ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
              </button>

              <Link to="/contact" className="hidden sm:block">
                <button className="h-10 px-6 rounded-full bg-primary text-sm font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary-hover hover:scale-105 active:scale-95 transition-all">
                  {t.nav.getQuote}
                </button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-gray-800 dark:text-white"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label="Toggle Mobile Menu"
              >
                <div className="relative w-6 h-5">
                  <span className={`absolute left-0 w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'top-2.5 rotate-45' : 'top-0'}`} />
                  <span className={`absolute left-0 w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'top-2.5'}`} />
                  <span className={`absolute left-0 w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'top-2.5 -rotate-45' : 'top-5'}`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Premium Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden bg-white/95 dark:bg-background-dark/95 backdrop-blur-xl transition-all duration-500 ease-in-out ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
          }`}
      >
        <div className="flex flex-col h-full pt-28 pb-10 px-6">
          <nav className="flex flex-col gap-6">
            {navLinks.map((link, idx) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`text-3xl font-bold tracking-tight transition-all duration-300 flex items-center justify-between group ${isMenuOpen ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                  }`}
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <span className={isActive(link.path) ? 'text-primary' : 'text-slate-900 dark:text-white'}>
                  {link.label}
                </span>
                <ArrowRight className={`opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all ${isActive(link.path) ? 'text-primary opacity-100 translate-x-0' : 'text-slate-900 dark:text-white'}`} />
              </Link>
            ))}
          </nav>

          <div className="mt-auto space-y-6">
            <div className="flex justify-center">
              <LanguageSelector />
            </div>
            <div className="w-full h-px bg-gray-200 dark:bg-white/10"></div>
            <div className="flex flex-col gap-4">
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <button className="w-full h-14 rounded-xl bg-primary text-lg font-bold text-white shadow-lg shadow-primary/25 active:scale-95 transition-all">
                  {t.nav.startProject}
                </button>
              </Link>
              <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="w-full h-14 flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                {t.nav.admin}
              </Link>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-400">
              <span>© 2024 GreySoftware S.r.l.</span>
              <div className="flex gap-4">
                {generalSettings.socials?.twitter && <a href={generalSettings.socials.twitter} className="hover:text-primary"><Twitter size={20} /></a>}
                {generalSettings.socials?.instagram && <a href={generalSettings.socials.instagram} className="hover:text-primary"><Instagram size={20} /></a>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-grow pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-border-dark bg-white dark:bg-background-dark pt-16 pb-8 text-sm relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4 mb-16">
            <div className="flex flex-col gap-6">
              <Link to="/" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
                  <Code size={18} />
                </div>
                <span className="text-xl font-bold text-slate-900 dark:text-white">GreySoftware</span>
              </Link>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                {siteContent.footer.desc}
              </p>
              <div className="flex gap-4">
                {generalSettings.socials?.github && (
                  <a href={generalSettings.socials.github} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-primary hover:text-white transition-all"><Github size={18} /></a>
                )}
                {generalSettings.socials?.twitter && (
                  <a href={generalSettings.socials.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-primary hover:text-white transition-all"><Twitter size={18} /></a>
                )}
                {generalSettings.socials?.linkedin && (
                  <a href={generalSettings.socials.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-primary hover:text-white transition-all"><Linkedin size={18} /></a>
                )}
                {generalSettings.socials?.instagram && (
                  <a href={generalSettings.socials.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-primary hover:text-white transition-all"><Instagram size={18} /></a>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <h4 className="font-bold text-slate-900 dark:text-white text-lg">{t.nav.services}</h4>
              <div className="flex flex-col gap-3">
                <Link to="/services" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.webDev}</Link>
                <Link to="/services" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.mobileApp}</Link>
                <Link to="/services" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.uiUx}</Link>
                <Link to="/services" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.consulting}</Link>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <h4 className="font-bold text-slate-900 dark:text-white text-lg">{t.nav.about}</h4>
              <div className="flex flex-col gap-3">
                <Link to="/about" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.nav.about}</Link>
                <Link to="/about" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.career}</Link>
                <Link to="/projects" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.footerLinks.blog}</Link>
                <Link to="/contact" className="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors w-fit">{t.nav.contact}</Link>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <h4 className="font-bold text-slate-900 dark:text-white text-lg">{t.nav.contact}</h4>
              <div className="flex flex-col gap-4">
                <p className="flex items-start gap-3 text-gray-500 dark:text-gray-400">
                  <MapPin size={20} className="shrink-0 text-primary mt-0.5" />
                  {generalSettings.address}
                </p>
                <p className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  <Mail size={20} className="shrink-0 text-primary" />
                  {generalSettings.contactEmail}
                </p>
                <p className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  <Phone size={20} className="shrink-0 text-primary" />
                  {generalSettings.contactPhone}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">{siteContent.footer.copyright}</p>
            <div className="flex gap-6 text-sm">
              <Link to="/about" className="text-gray-500 hover:text-primary transition-colors">{t.footerLinks.privacy}</Link>
              <Link to="/about" className="text-gray-500 hover:text-primary transition-colors">{t.footerLinks.terms}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};